import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { FlaskConical, Plus, Copy, CopyPlus, BarChart3, Trophy, X, Upload, Loader2, Power, ImageDown, Users, FileDown } from "lucide-react";
import api, { API } from "../lib/api";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";
import { jsPDF } from "jspdf";

const fileUrl = (p) => (p ? `${API}/labs/files/${p}` : null);

function CreateForm({ onCreated }) {
  const [brand, setBrand] = useState("");
  const [headline, setHeadline] = useState("");
  const [question, setQuestion] = useState("");
  const [format, setFormat] = useState("head_to_head");
  const [options, setOptions] = useState([{ label: "", image_path: null }, { label: "", image_path: null }]);
  const [subjectLabel, setSubjectLabel] = useState("");
  const [subjectImage, setSubjectImage] = useState(null);
  const [logo, setLogo] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState("");

  const upload = async (file, setter, key) => {
    setUploading(key);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/admin/campaigns/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setter(r.data.path);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    finally { setUploading(""); }
  };

  const setOpt = (i, patch) => setOptions((o) => o.map((x, idx) => (idx === i ? { ...x, ...patch } : x)));
  const reactionDefaults = () => setOptions([{ label: "Looks premium", image_path: null }, { label: "Looks cheap", image_path: null }, { label: "Would buy it", image_path: null }]);

  const create = async () => {
    if (!brand.trim() || !question.trim()) return toast.error("Brand name and question are required");
    const opts = options.filter((o) => o.label.trim());
    if (opts.length < 2) return toast.error("At least 2 options");
    setSaving(true);
    try {
      const body = { brand_name: brand.trim(), headline: headline.trim() || null, question: question.trim(), format,
        options: opts, brand_logo_path: logo, subject_label: format === "reaction_poll" ? subjectLabel.trim() || null : null,
        subject_image_path: format === "reaction_poll" ? subjectImage : null };
      const r = await api.post("/admin/campaigns", body);
      toast.success("Campaign created 🎉");
      setBrand(""); setHeadline(""); setQuestion(""); setOptions([{ label: "", image_path: null }, { label: "", image_path: null }]);
      setLogo(null); setSubjectImage(null); setSubjectLabel("");
      onCreated(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  const UploadBtn = ({ path, onDone, k }) => (
    <label className="cursor-pointer inline-flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-sm border border-white/15 hover:border-brand/50 text-zinc-400">
      {uploading === k ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />} {path ? "Change" : "Image"}
      <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files[0] && upload(e.target.files[0], onDone, k)} />
    </label>
  );

  return (
    <div className="rounded-sm border border-white/10 bg-zinc-950/40 p-4 mb-5" data-testid="labs-create-form">
      <div className="grid sm:grid-cols-2 gap-3">
        <input data-testid="labs-brand" value={brand} onChange={(e) => setBrand(e.target.value)} placeholder="Brand name (e.g. Zaru Cold Brew)" className="bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none" />
        <div className="flex items-center gap-2">
          <input data-testid="labs-headline" value={headline} onChange={(e) => setHeadline(e.target.value)} placeholder="Headline (optional)" className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none" />
          <UploadBtn path={logo} onDone={setLogo} k="logo" />
        </div>
      </div>
      <input data-testid="labs-question" value={question} onChange={(e) => setQuestion(e.target.value)} placeholder="The question (e.g. Which can design should we launch?)" className="mt-3 w-full bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none" />
      <div className="flex gap-2 mt-3">
        {[["head_to_head", "Head-to-head (A/B/C)"], ["reaction_poll", "Reaction poll"]].map(([v, l]) => (
          <button key={v} data-testid={`labs-format-${v}`} onClick={() => { setFormat(v); if (v === "reaction_poll") reactionDefaults(); else setOptions([{ label: "", image_path: null }, { label: "", image_path: null }]); }}
            className={`text-xs px-3 py-1.5 rounded-full border ${format === v ? "border-brand bg-brand/10 text-white" : "border-white/15 text-zinc-400"}`}>{l}</button>
        ))}
      </div>

      {format === "reaction_poll" && (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="reaction-presets">
          {[
            { k: "packaging", label: "Packaging", q: "First impression of this packaging?", subj: "the new packaging", opts: ["Looks premium", "Looks cheap", "Would buy it"] },
            { k: "pricing", label: "Pricing", q: "How does this price feel?", subj: "the price", opts: ["Too expensive", "Fair price", "A steal"] },
            { k: "naming", label: "Naming", q: "What do you think of this name?", subj: "the name", opts: ["Love it", "It's fine", "Hate it"] },
          ].map((p) => (
            <button key={p.k} data-testid={`preset-${p.k}`} type="button"
              onClick={() => { if (!question.trim()) setQuestion(p.q); setSubjectLabel(p.subj); setOptions(p.opts.map((l) => ({ label: l, image_path: null }))); }}
              className="text-xs px-3 py-1.5 rounded-full border border-brand/40 text-brand hover:bg-brand/10 transition-colors">
              ⚡ {p.label} preset
            </button>
          ))}
        </div>
      )}

      {format === "reaction_poll" && (
        <div className="mt-3 flex items-center gap-2">
          <input value={subjectLabel} onChange={(e) => setSubjectLabel(e.target.value)} placeholder="What are they reacting to? (label)" className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none" />
          <UploadBtn path={subjectImage} onDone={setSubjectImage} k="subject" />
          {subjectImage && <img src={fileUrl(subjectImage)} alt="" className="w-9 h-9 rounded object-cover" />}
        </div>
      )}

      <div className="mt-3 space-y-2">
        {options.map((o, i) => (
          <div key={i} className="flex items-center gap-2">
            <input data-testid={`labs-option-${i}`} value={o.label} onChange={(e) => setOpt(i, { label: e.target.value })} placeholder={`Option ${i + 1}`} className="flex-1 bg-zinc-900 border border-zinc-800 rounded-sm p-2 text-sm focus:border-brand focus:outline-none" />
            {format === "head_to_head" && <UploadBtn path={o.image_path} onDone={(p) => setOpt(i, { image_path: p })} k={`opt${i}`} />}
            {o.image_path && <img src={fileUrl(o.image_path)} alt="" className="w-9 h-9 rounded object-cover" />}
            {options.length > 2 && <button onClick={() => setOptions((x) => x.filter((_, idx) => idx !== i))} className="text-zinc-600 hover:text-brand"><X size={15} /></button>}
          </div>
        ))}
        <button data-testid="labs-add-option" onClick={() => setOptions((o) => [...o, { label: "", image_path: null }])} className="text-xs text-brand inline-flex items-center gap-1"><Plus size={13} /> Add option</button>
      </div>

      <button data-testid="labs-create-btn" onClick={create} disabled={saving} className="mt-4 px-5 py-2.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-sm transition-colors disabled:opacity-50">
        {saving ? "Creating…" : "Create Brand Challenge"}
      </button>
    </div>
  );
}

function Report({ campaign, onClose }) {
  const [data, setData] = useState(null);
  const { share, busyId, shareRef, shareItem } = useRoastShare();
  useEffect(() => { api.get(`/admin/campaigns/${campaign.id}/report`).then((r) => setData(r.data)).catch(() => {}); }, [campaign.id]);
  const recap = () => {
    if (!data) return;
    const wpct = (data.options.find((o) => o.id === data.winner_id) || {}).pct || 0;
    share({ kind: "labs_result", brand: campaign.brand_name, question: campaign.question, winner: data.winner_label,
      winner_pct: wpct, participants: data.participants, top_comment: data.comments?.[0]?.comment || null }, "labs_result");
  };
  const exportPdf = () => {
    if (!data) return;
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const M = 48; let y = 56; const W = 595 - M * 2;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(255, 69, 0);
    doc.text("ROASTLIT LABS · REALITY CHECK", M, y); y += 26;
    doc.setTextColor(20, 20, 20); doc.setFontSize(20); doc.text(campaign.brand_name, M, y); y += 22;
    doc.setFont("helvetica", "normal"); doc.setFontSize(13); doc.setTextColor(60, 60, 60);
    doc.splitTextToSize(campaign.question, W).forEach((ln) => { doc.text(ln, M, y); y += 18; });
    y += 6; doc.setDrawColor(220); doc.line(M, y, M + W, y); y += 22;
    doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.setTextColor(20, 20, 20);
    doc.text(`${data.participants} participants · Winner: ${data.winner_label || "—"}`, M, y); y += 22;
    doc.setFontSize(11);
    data.options.forEach((o) => {
      doc.setFont("helvetica", "bold"); doc.setTextColor(20, 20, 20);
      doc.text(`${o.label}`, M, y); doc.text(`${o.pct}%  (${o.count})`, M + W - 90, y);
      doc.setFillColor(240, 240, 240); doc.rect(M, y + 5, W, 7, "F");
      doc.setFillColor(255, 69, 0); doc.rect(M, y + 5, (W * o.pct) / 100, 7, "F"); y += 26;
    });
    if (data.demographics && (data.demographics.age?.length || data.demographics.city?.length)) {
      y += 8; doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(20, 20, 20);
      doc.text(`Who responded (self-declared, ${data.demographics.provided})`, M, y); y += 18;
      doc.setFont("helvetica", "normal"); doc.setFontSize(10); doc.setTextColor(70, 70, 70);
      const age = (data.demographics.age || []).map((a) => `${a.label}: ${a.count}`).join("   ");
      const city = (data.demographics.city || []).map((a) => `${a.label}: ${a.count}`).join("   ");
      if (age) { doc.text(`Age — ${age}`, M, y); y += 16; }
      if (city) { doc.text(`City — ${city}`, M, y); y += 16; }
    }
    if (data.comments?.length) {
      y += 8; doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(20, 20, 20);
      doc.text("Top comments (anonymous)", M, y); y += 16;
      doc.setFont("helvetica", "italic"); doc.setFontSize(10); doc.setTextColor(70, 70, 70);
      data.comments.slice(0, 6).forEach((c) => {
        doc.splitTextToSize(`“${c.comment}” — ${c.option_label}`, W).forEach((ln) => { if (y < 780) { doc.text(ln, M, y); y += 14; } });
      });
    }
    doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(150);
    doc.text("Aggregated only · no individual identities · groups below the minimum size are hidden · roastlit.app/labs", M, 812);
    doc.save(`RoastLit-Labs-${campaign.brand_name.replace(/\s+/g, "-")}.pdf`);
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-lg max-h-[85vh] overflow-y-auto rounded-lg border border-white/10 bg-zinc-950 p-6" onClick={(e) => e.stopPropagation()} data-testid="labs-report">
        <div className="flex items-center justify-between mb-1"><span className="text-brand-accent text-xs uppercase tracking-widest font-bold">{campaign.brand_name}</span><button onClick={onClose}><X size={18} className="text-zinc-500" /></button></div>
        <h3 className="font-heading font-bold text-xl mb-4">{campaign.question}</h3>
        {!data ? <Loader2 className="animate-spin text-brand" /> : (
          <>
            <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
              <div className="text-sm text-zinc-400"><b className="text-white">{data.participants}</b> participants · winner <b className="text-brand-accent">{data.winner_label || "—"}</b></div>
              <button data-testid="labs-recap-btn" onClick={recap} disabled={busyId === "labs_result"}
                className="inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-xs px-3 py-2 transition-colors disabled:opacity-60">
                <ImageDown size={13} /> {busyId === "labs_result" ? "Preparing…" : "Recap card"}
              </button>
              <button data-testid="labs-pdf-btn" onClick={exportPdf}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-brand/50 text-zinc-200 font-bold text-xs px-3 py-2 transition-colors">
                <FileDown size={13} /> Download PDF
              </button>
            </div>
            <div className="space-y-2">
              {data.options.map((o) => (
                <div key={o.id} className="rounded-sm border border-white/10 bg-zinc-900/60 p-3">
                  <div className="flex justify-between text-sm"><span className="font-bold">{o.label}</span><span className="text-zinc-400">{o.pct}% · {o.count}</span></div>
                  <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-brand rounded-full" style={{ width: `${o.pct}%` }} /></div>
                </div>
              ))}
            </div>
            {data.demographics && (data.demographics.age?.length > 0 || data.demographics.city?.length > 0) && (
              <div className="mt-5" data-testid="labs-demographics">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-2"><Users size={13} /> Who responded (self-declared, {data.demographics.provided} shared)</div>
                {data.demographics.age?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {data.demographics.age.map((a) => <span key={a.label} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10">{a.label}: <b>{a.count}</b></span>)}
                  </div>
                )}
                {data.demographics.city?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {data.demographics.city.map((a) => <span key={a.label} className="text-xs px-2.5 py-1 rounded-full bg-white/5 border border-white/10">{a.label}: <b>{a.count}</b></span>)}
                  </div>
                )}
              </div>
            )}
            {data.comments?.length > 0 && (
              <div className="mt-5">
                <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">Top comments (anonymous)</div>
                <div className="space-y-1.5">
                  {data.comments.slice(0, 10).map((c, i) => <div key={i} className="text-sm text-zinc-300 border-l-2 border-brand/40 pl-3">"{c.comment}" <span className="text-zinc-600 text-xs">— {c.option_label}</span></div>)}
                </div>
              </div>
            )}
            <div className="mt-5 text-[11px] text-zinc-600">Aggregated only — no voter identities are ever shown or shared with the brand. Demographic groups below the minimum size are hidden.</div>
          </>
        )}
      </div>
      <ShareCardPortal shareRef={shareRef} item={shareItem} />
    </div>
  );
}

export default function AdminLabs() {
  const [list, setList] = useState([]);
  const [report, setReport] = useState(null);

  const load = useCallback(() => { api.get("/admin/campaigns").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { load(); }, [load]);

  const copyLink = (slug) => { navigator.clipboard.writeText(`${window.location.origin}/labs/${slug}`); toast.success("Link copied"); };
  const toggle = async (c) => { try { await api.post(`/admin/campaigns/${c.id}/close`); load(); } catch (e) { toast.error("Failed"); } };
  const duplicate = async (c) => { try { await api.post(`/admin/campaigns/${c.id}/duplicate`); toast.success("Duplicated — edit the copy as a starting point"); load(); } catch (e) { toast.error(e.response?.data?.detail || "Failed"); } };

  return (
    <div className="mb-12" data-testid="admin-labs">
      <div className="flex items-center gap-2 mb-1"><FlaskConical size={16} className="text-brand" /><h2 className="text-xs tracking-[0.2em] uppercase text-zinc-400 font-bold">RoastLit Labs — Brand Challenges</h2></div>
      <p className="text-xs text-zinc-600 mb-4">Create a branded vote link the brand shares with its own audience. Results are aggregated only.</p>
      <CreateForm onCreated={load} />
      <div className="space-y-2">
        {list.length === 0 && <div className="text-sm text-zinc-600">No campaigns yet.</div>}
        {list.map((c) => (
          <div key={c.id} data-testid={`labs-campaign-${c.id}`} className="rounded-sm border border-white/10 bg-zinc-900/60 p-4 flex items-center justify-between gap-3 flex-wrap">
            <div className="min-w-0">
              <div className="flex items-center gap-2"><span className="font-bold truncate">{c.brand_name}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full uppercase tracking-widest ${c.status === "active" ? "bg-tier-mild/15 text-tier-mild" : "bg-zinc-700/40 text-zinc-400"}`}>{c.status}</span></div>
              <div className="text-sm text-zinc-400 truncate">{c.question}</div>
              <div className="text-xs text-zinc-600 mt-0.5">/labs/{c.slug} · {c.votes} votes</div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button data-testid={`labs-copy-${c.id}`} onClick={() => copyLink(c.slug)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 hover:border-brand/50 text-zinc-300"><Copy size={12} /> Link</button>
              <button data-testid={`labs-report-${c.id}`} onClick={() => setReport(c)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 hover:border-brand/50 text-zinc-300"><BarChart3 size={12} /> Report</button>
              <button data-testid={`labs-duplicate-${c.id}`} onClick={() => duplicate(c)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 hover:border-brand/50 text-zinc-300"><CopyPlus size={12} /> Duplicate</button>
              <button onClick={() => toggle(c)} className="text-xs inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-white/15 hover:border-brand/50 text-zinc-300"><Power size={12} /> {c.status === "active" ? "Close" : "Reopen"}</button>
            </div>
          </div>
        ))}
      </div>
      {report && <Report campaign={report} onClose={() => setReport(null)} />}
    </div>
  );
}
